import type { PerformanceTimePeriodName, TaskTypeName } from '@savvy/constants/generated/gql-enums';
import {
  PERFORMANCE_TIME_PERIOD_ALL_TIME,
  PERFORMANCE_TIME_PERIOD_PAST_MONTH,
  PERFORMANCE_TIME_PERIOD_PAST_THREE_MONTHS,
  PERFORMANCE_TIME_PERIOD_PAST_YEAR,
  PERFORMANCE_TIME_PERIOD_YEAR_TO_DATE,
} from '@savvy/constants/performance-time-periods';
import {
  TASK_TYPE_ACCOUNT_SETUP,
  TASK_TYPE_INITIAL_INTAKE_FORM,
  TASK_TYPE_ONBOARDING_FLOW,
} from '@savvy/constants/task-types';

export const CLIENT_PORTAL_TIME_RANGES = [
  { value: PERFORMANCE_TIME_PERIOD_PAST_MONTH, label: '1M' },
  { value: PERFORMANCE_TIME_PERIOD_PAST_THREE_MONTHS, label: '3M' },
  { value: PERFORMANCE_TIME_PERIOD_YEAR_TO_DATE, label: 'YTD' },
  { value: PERFORMANCE_TIME_PERIOD_PAST_YEAR, label: '1Y' },
  { value: PERFORMANCE_TIME_PERIOD_ALL_TIME, label: 'ALL' },
] as const satisfies readonly { value: PerformanceTimePeriodName; label: string }[];

export type ClientPortalTimeRange = (typeof CLIENT_PORTAL_TIME_RANGES)[number]['value'];

export type ClientPortalTimeRangeControl = {
  readonly preferredTimeRange: ClientPortalTimeRange;
  readonly setTimeRange: (timeRange: ClientPortalTimeRange) => void;
};

const CLIENT_PORTAL_TIME_RANGE_VALUES: readonly string[] = CLIENT_PORTAL_TIME_RANGES.map((range) => range.value);

export function isClientPortalTimeRange(value: string): value is ClientPortalTimeRange {
  return CLIENT_PORTAL_TIME_RANGE_VALUES.includes(value);
}

export const CLIENT_PORTAL_DEFAULT_TIME_RANGE: ClientPortalTimeRange = PERFORMANCE_TIME_PERIOD_PAST_YEAR;

export const CLIENT_PORTAL_EXCLUDED_TASK_TYPES: readonly TaskTypeName[] = [
  TASK_TYPE_INITIAL_INTAKE_FORM,
  TASK_TYPE_ONBOARDING_FLOW,
  TASK_TYPE_ACCOUNT_SETUP,
];

export const CLIENT_PORTAL_ALLOCATION_COLORS: readonly string[] = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

export const CLIENT_PORTAL_MONOGRAM_COLORS: readonly string[] = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];

export const CLIENT_PORTAL_BIG_NUMBER_CLASS = 'font-semibold leading-none tracking-tight tabular-nums';

const HEADLINE_NUMBER_TAIL = 'leading-none font-semibold tracking-tight tabular-nums';

export const CLIENT_PORTAL_HEADLINE_NUMBER_CLASS = `text-[32px] ${HEADLINE_NUMBER_TAIL}`;

export const CLIENT_PORTAL_HEADLINE_NUMBER_RESPONSIVE_CLASS = `text-3xl md:text-[32px] ${HEADLINE_NUMBER_TAIL}`;

export const CLIENT_PORTAL_PAGE_PADDING_CLASS = 'px-4 pb-6 md:px-10 md:pb-10';

// Axis labels ease out fast (AXIS.out), swap while invisible, and ease back in slowly (AXIS.in) on a range switch,
// while the curve morphs underneath.
export const CLIENT_PORTAL_CHART_AXIS_FADE_CLASS =
  '[&_.recharts-cartesian-axis-tick-value]:transition-opacity [&_.recharts-cartesian-axis-tick-value]:duration-[420ms]';

export const CLIENT_PORTAL_CHART_AXIS_DIM_CLASS =
  '[&_.recharts-cartesian-axis-tick-value]:opacity-0 [&_.recharts-cartesian-axis-tick-value]:duration-[170ms]';

export const CLIENT_PORTAL_PAGE_HEADING_CLASS = 'max-md:!text-[1.75rem] max-md:!leading-[2.25rem]';

export const CLIENT_PORTAL_BANNER_TITLE_WRAP_CLASS = 'max-md:[&_[data-slot=alert-title]]:line-clamp-none';

export const CLIENT_PORTAL_RETRY_LABEL = 'Try again';

export const CLIENT_PORTAL_RETRY_DESCRIPTION = 'Try again in a moment.';

export const CLIENT_PORTAL_UNCATEGORISED_ASSET_CLASS_LABEL = 'Uncategorised';

export const BENCHMARK_NAME_QUALIFIERS: readonly string[] = ['TR', 'NR', 'PR', 'GR', 'USD', 'Index'];

export const CLIENT_PORTAL_TRANSFER_CUSTODIAN_LABELS = new Map<string, string>([
  ['fidelity', 'Fidelity'],
  ['fidelity_iws', 'Fidelity'],
  ['fidelity_nfs', 'Fidelity'],
  ['goldman_sachs', 'Goldman Sachs'],
  ['schwab', 'Schwab'],
]);

export const CLIENT_PORTAL_TRANSFER_CUSTODIAN_FALLBACK_LABEL = 'your new custodian';

export function clientPortalTransferCustodianLabel(custodian: string | null | undefined): string {
  const key = (custodian ?? '').trim().toLowerCase();

  return CLIENT_PORTAL_TRANSFER_CUSTODIAN_LABELS.get(key) ?? CLIENT_PORTAL_TRANSFER_CUSTODIAN_FALLBACK_LABEL;
}

export function clientPortalTransferFooterNote(custodian: string | null | undefined): string {
  return `Transferring to ${clientPortalTransferCustodianLabel(custodian)} · your assets stay invested the whole time`;
}

export const CLIENT_PORTAL_TRANSFER_INFO_LABEL = 'About your transfer';

export const CLIENT_PORTAL_TRANSFER_INFO_TITLE = 'Your transfer, explained';

export function clientPortalTransferInfoBody(custodian: string | null | undefined): readonly [string, string] {
  return [
    'Your assets stay invested for the entire transfer — nothing is sold to move them.',
    `Balances can look incomplete until every account has settled at ${clientPortalTransferCustodianLabel(custodian)}. Your advisor is tracking it, and there is nothing you need to do.`,
  ];
}

export function clientPortalTransferRibbonMessage(custodian: string | null | undefined): string {
  return `Transfer in progress. Your assets are moving to ${clientPortalTransferCustodianLabel(custodian)} and stay invested the whole time.`;
}

export const CLIENT_PORTAL_DIALOG_PANEL_CLASS =
  'flex max-h-[calc(var(--sk-visible-viewport-height,calc(100svh-6rem))-2rem)] flex-col gap-0 overflow-hidden p-0';
